import {Stack} from 'src/design/atoms/Stack';
import {Box} from 'src/design/atoms/Box';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {BulletinRequest} from 'src/api/ProactApiClient';
import {EntityIdentifierEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {
  useCreateBulletinMutation,
  useDeleteBulletinMutation,
  useGetBulletinQuery,
  useUpdateBulletinMutation,
} from 'src/redux/queries/bulletins';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SidebarLinkBulletins} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {excludeContentFields} from 'src/app/content-pdf/contentPdf';
import {ContentBody} from 'src/app/content-pdf/ContentBody';
import {ContentEditorSection} from 'src/app/content-pdf/ContentEditorSection';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {
  BulletinFormMeta,
  CreateDefaultBulletin,
  GetBulletinPillConfig,
} from '../bulletinHelpers';
import {useBulletinsBreadcrumbs} from './hooks/useBulletinsBreadcrumbs';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type BulletinViewType = 'content' | 'audit' | ':view' | '';

export interface BulletinFormProps {
  id?: number;
  mode: 'create' | 'edit' | 'view';
}

export const BulletinForm: React.FC<BulletinFormProps> = ({id, mode}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const {view} = useParams();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const [isSavePending, setIsSavePending] = useState(false);

  const formMeta = useMemo(() => BulletinFormMeta(), []);
  const detailsMeta = useMemo(() => excludeContentFields(formMeta), [formMeta]);

  const perms = useModulePermissions(ModuleEnum.Bulletins);
  const auditPerms = useAuditLogPermissions();

  const [createBulletin] = useCreateBulletinMutation();
  const [updateBulletin] = useUpdateBulletinMutation();
  const [deleteBulletin] = useDeleteBulletinMutation();

  const {
    data: item,
    isLoading,
    error: bulletinFetchError,
  } = useGetBulletinQuery(id!, {skip: id === undefined});

  const form = useProactForm({
    formMeta,
    initialData: CreateDefaultBulletin(),
  });
  const {resetForm} = form;

  const viewFormData = useMemo(() => toFormDataV2(item), [item]);

  useEffect(() => {
    if (id === undefined) {
      resetForm(toFormDataV2(CreateDefaultBulletin()));
      return;
    }
    if (item) {
      resetForm(
        toFormDataV2({
          ...CreateDefaultBulletin(),
          ...item,
          contentPdfUrl: item.contentPdfUrl ?? undefined,
        })
      );
    }
  }, [id, item, resetForm]);

  useEffect(() => {
    if (bulletinFetchError) {
      setAlert(parseErrorMessage(bulletinFetchError), 'error');
    }
  }, [bulletinFetchError, setAlert]);

  const {backBarProps} = useBulletinsBreadcrumbs({
    mode,
    id,
    title: item?.title,
    view: view as BulletinViewType,
  });

  const pills = useMemo(() => GetBulletinPillConfig(item), [item]);

  const handleSave = async () => {
    try {
      setIsSavePending(true);
      const values = form.getFieldValues();
      const body = {
        ...item,
        ...values,
        contentPdfUrl: values.contentPdfUrl || undefined,
      } as BulletinRequest;

      const res =
        id === undefined
          ? await createBulletin(body).unwrap()
          : await updateBulletin(body).unwrap();

      setAlert('Bulletin was successfully updated', 'success');

      navigate(PathUtility.BulletinView(res.id, view as BulletinViewType), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    if (await form.validateForm()) {
      void handleSave();
    }
  };

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!id) {
        return;
      }

      setIsSavePending(true);

      await deleteBulletin(id).unwrap();

      setAlert('Bulletin was successfully deleted', 'success');

      navigate(PathUtility.BulletinDashboard('manage'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleEdit = () =>
    navigate(PathUtility.BulletinEdit(String(id), view as BulletinViewType));
  const handleDeleteClick = () => confirmDeleteHandlers.open();

  const {RenderedTabsComponent} = useUrlQueryTabV2<BulletinViewType>({
    view: (view ?? '') as BulletinViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            {mode !== 'view' ? (
              <>
                <ProactForm form={form}>
                  <Stack gap="md" mb="xl">
                    <SectionCard title="Details" data-testid="section-details">
                      <Box p="md">
                        <ProactFormGrid
                          fieldNames={detailsMeta.map(field => field.name)}
                          mb={0}
                        />
                      </Box>
                    </SectionCard>
                    <ContentEditorSection />
                  </Stack>
                </ProactForm>
                <SaveButton
                  loading={isSavePending}
                  onClick={handleValidateAndSave}
                />
              </>
            ) : (
              <Stack gap="md">
                {detailsMeta.length > 0 && (
                  <SectionCard title="Details" data-testid="section-details">
                    <Box p="md">
                      <FormViewComponentV2
                        formData={viewFormData}
                        formMeta={detailsMeta}
                        spacing="compact"
                      />
                    </Box>
                  </SectionCard>
                )}
                <ContentBody
                  content={item?.content}
                  contentPdfUrl={item?.contentPdfUrl}
                />
              </Stack>
            )}
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: mode !== 'view' || !auditPerms.view,
        component: item?.id && (
          <AuditModule
            entityId={String(item.id)}
            entityType={EntityIdentifierEnum.Bulletin}
          />
        ),
      },
    ],
    onSelectView: view =>
      mode === 'create'
        ? navigate(PathUtility.BulletinCreate(view as BulletinViewType), {
            replace: true,
          })
        : mode === 'edit'
        ? navigate(
            PathUtility.BulletinEdit(String(id), view as BulletinViewType),
            {replace: true}
          )
        : navigate(
            PathUtility.BulletinView(String(id), view as BulletinViewType),
            {replace: true}
          ),
  });

  const isViewMode = mode === 'view';
  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  return !isLoading ? (
    <Stack gap="md">
      <BackBar {...backBarProps} />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkBulletins.icon}>
          {item ? item.title : 'New Bulletin'}
        </PageHeader.Title>

        {isViewMode && pills.length > 0 && (
          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>
        )}

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          {isViewMode && perms.update && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}
          {isViewMode && perms.delete && (
            <PageHeader.Action variant="delete" onClick={handleDeleteClick}>
              Delete
            </PageHeader.Action>
          )}
          {(isCreateMode || isEditMode) && (
            <PageHeader.Action
              variant={isCreateMode ? 'create' : 'save'}
              onClick={handleValidateAndSave}
              loading={isSavePending}>
              {isCreateMode ? 'Create Bulletin' : 'Save Changes'}
            </PageHeader.Action>
          )}
        </PageHeader.Actions>
      </PageHeader>

      {RenderedTabsComponent}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this bulletin?"
        body="The bulletin will be permanently deleted."
      />
    </Stack>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
